import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { map, Observable, shareReplay } from 'rxjs';
import { AppConfigService } from '@shared-module/services/app-config/app-config.service';

const COUNTRY_NAMES_LOCALE = 'es';

export interface ICountry {
  code: string;
  name: string;
}

interface ICountriesResponse {
  countries: ICountry[];
}

@Injectable({
  providedIn: 'root',
})
export class CountriesService {
  private readonly _app = inject(AppConfigService);
  private readonly _httpClient = inject(HttpClient);
  private _countries$: Observable<ICountry[]> | null = null;

  private readonly _regionNames = new Intl.DisplayNames(
    [COUNTRY_NAMES_LOCALE],
    { type: 'region', fallback: 'none' }
  );

  // Backend returns the SAP list, whose names are in English. Names are
  // translated here from the country code. Cached so every screen shares a
  // single request; shareReplay resets on error, so a failed request is
  // retried on next use.
  public getCountries(): Observable<ICountry[]> {
    this._countries$ ??= this._httpClient
      .get<ICountriesResponse>(
        `${this._app.authBaseUrl}/viajeros/paisesOrigen/countries`
      )
      .pipe(
        map(response =>
          this._toSpanishSortedCountries(response?.countries ?? [])
        ),
        shareReplay({ bufferSize: 1, refCount: false })
      );
    return this._countries$;
  }

  private _toSpanishSortedCountries(countries: ICountry[]): ICountry[] {
    return countries
      .map(country => ({
        ...country,
        name: this._translateName(country),
      }))
      .sort((a, b) => a.name.localeCompare(b.name, COUNTRY_NAMES_LOCALE));
  }

  // Falls back to the SAP name when the code is not a valid ISO region.
  private _translateName({ code, name }: ICountry): string {
    try {
      return this._regionNames.of(code) ?? name;
    } catch {
      return name;
    }
  }
}
